import { useEffect, useRef, useState } from "react";
import { useReplicache } from "../replicache/context";
import type { Note } from "../replicache/keys";

// Single-textarea notepad shown in the main pane when a Note is selected
// in the sidebar.  Edits are buffered locally and flushed to Replicache
// on a short debounce so each keystroke doesn't fire a mutation (and
// to avoid thrashing the sync hash on a long typing burst).  When the
// note changes underneath us — e.g. another device pushes a server
// update — we reconcile cautiously: if the user hasn't typed since the
// last save, accept the incoming value; otherwise hold local edits
// until the user pauses again, so we don't yank text out from under
// their cursor.

const SAVE_DEBOUNCE_MS = 400;

export function NotepadView({
  note,
  onClose,
}: {
  note: Note;
  onClose: () => void;
}) {
  const rep = useReplicache();

  // Local buffers for the inputs.  Initialised from the note; we never
  // re-derive on every render so the user can type without server
  // updates fighting them.
  const [name, setName] = useState(note.name);
  const [body, setBody] = useState(note.body);
  // True between the user typing and the debounce flush completing.  We
  // use this to suppress reconciliation from server pulls so they don't
  // overwrite the in-flight edit.
  const dirtyRef = useRef(false);
  // Whether the caret is currently in each field.  `dirtyRef` alone isn't
  // enough to gate reconciliation: it clears the instant a debounced save
  // flushes (see below), so a server pull landing in that brief post-flush
  // window would call setName/setBody while the user is still typing —
  // reassigning the controlled value, jumping the caret to the end, and
  // scrolling the current line to the top of the pane.  Never overwrite a
  // field the user is actively focused in.
  const nameFocused = useRef(false);
  const bodyFocused = useRef(false);
  // Track which note we're showing so a sidebar switch resets the
  // buffers instead of carrying stale draft from the previous note.
  const noteIdRef = useRef(note.id);

  useEffect(() => {
    if (noteIdRef.current !== note.id) {
      noteIdRef.current = note.id;
      dirtyRef.current = false;
      setName(note.name);
      setBody(note.body);
      return;
    }
    // Same note id: reconcile from the server only if the local buffer
    // isn't dirty (another device's edit landing while we sit idle) and the
    // user isn't currently typing in the field — overwriting a focused
    // field's value yanks the caret and scrolls the pane.
    if (!dirtyRef.current) {
      if (!nameFocused.current && note.name !== name) setName(note.name);
      if (!bodyFocused.current && note.body !== body) setBody(note.body);
    }
    // We intentionally do NOT depend on `name`/`body` here — that would
    // turn this into a comparison loop.  Re-running only on note prop
    // changes is the desired behaviour.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [note]);

  // Debounced flush.  Captures the current buffer values in the timer
  // closure so a fast-fire of edits coalesces to one mutation.
  useEffect(() => {
    if (!rep) return;
    if (!dirtyRef.current) return;
    const id = window.setTimeout(() => {
      void (async () => {
        const patch: { id: string; name?: string; body?: string } = {
          id: note.id,
        };
        if (name !== note.name) patch.name = name;
        if (body !== note.body) patch.body = body;
        if (patch.name !== undefined || patch.body !== undefined) {
          await rep.mutate.updateNote(patch);
        }
        dirtyRef.current = false;
      })();
    }, SAVE_DEBOUNCE_MS);
    return () => window.clearTimeout(id);
  }, [name, body, rep, note.id, note.name, note.body]);

  // Flush any pending edit on unmount / note switch so a quick switch
  // away doesn't lose the last-typed character.
  useEffect(() => {
    return () => {
      if (!rep || !dirtyRef.current) return;
      void rep.mutate.updateNote({ id: note.id, name, body });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [note.id]);

  const onDelete = async () => {
    if (!rep) return;
    if (
      !confirm(
        `Delete note "${note.name || "Untitled"}"? This cannot be undone.`,
      )
    ) {
      return;
    }
    await rep.mutate.deleteNote({ id: note.id });
    onClose();
  };

  return (
    <section className="flex flex-1 flex-col overflow-hidden">
      <header className="flex items-center gap-2 border-b border-slate-200 px-4 py-2 sm:px-6 dark:border-slate-700">
        <button
          type="button"
          onClick={onClose}
          title="Back to Notes"
          aria-label="Back to Notes"
          className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:text-slate-500 dark:hover:bg-slate-800 dark:hover:text-slate-200"
        >
          ←
        </button>
        <span aria-hidden className="text-base">
          📝
        </span>
        <input
          value={name}
          onChange={(e) => {
            dirtyRef.current = true;
            setName(e.target.value);
          }}
          onFocus={() => (nameFocused.current = true)}
          onBlur={() => (nameFocused.current = false)}
          placeholder="Untitled note"
          className="flex-1 rounded border border-transparent bg-transparent px-1 py-1 text-lg font-semibold tracking-tight focus:border-slate-300 focus:bg-white focus:outline-none dark:focus:border-slate-600 dark:focus:bg-slate-800"
        />
        <span className="hidden text-[11px] text-slate-400 sm:inline dark:text-slate-500">
          Auto-saved · offline-synced
        </span>
        <button
          type="button"
          onClick={() => void onDelete()}
          className="rounded px-2 py-0.5 text-xs font-medium text-rose-600 hover:bg-rose-50 hover:text-rose-800 dark:text-rose-400 dark:hover:bg-rose-950 dark:hover:text-rose-300"
        >
          Delete
        </button>
      </header>

      <textarea
        value={body}
        onChange={(e) => {
          dirtyRef.current = true;
          setBody(e.target.value);
        }}
        onFocus={() => (bodyFocused.current = true)}
        onBlur={() => (bodyFocused.current = false)}
        placeholder="Start typing…"
        className="flex-1 resize-none border-0 bg-white px-4 py-3 font-mono text-sm leading-relaxed text-slate-900 focus:outline-none sm:px-6 dark:bg-slate-800 dark:text-slate-100"
        spellCheck
      />
    </section>
  );
}
